/**
 * #814 — Pricing rules: owner-managed automatic discounts & surcharges.
 *
 * Ted's ask (2026-08-17): "Gingr already knows the dog was there 10 days →
 * 10% discount… applied globally for certain things — 10-night stay, two
 * dogs." This is the management surface for the tenant-scoped PricingRule
 * engine: define a rule once, every eligible booking prices itself.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: every rule row leads with the HUMAN sentence
 *    the rule means ("10% off Boarding — stays of 10+ nights") derived from
 *    the same fields the engine executes — no second phrasing to drift.
 *  - #2 The next action is obvious: one primary "New rule" button; non-owner
 *    roles get a plain read-only reason, never a dead control (#684 class).
 *  - #5 Never lie about data: loading is a skeleton, a failed load is an
 *    honest error with Retry, an empty tenant sees a real empty state, and
 *    platform-wide rules are labelled read-only instead of failing on edit.
 *  - Five states: loading · error(+retry) · empty · ready · saving.
 *
 * Server contract: /api/bookings/admin/pricing-rules (booking admin guard).
 * Reads business-config:read:all; writes business-config.pricing:write:all
 * (owner-only). Foreign/platform rules 404 on write server-side — the UI
 * never offers those actions in the first place.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
} from '@ferro/ui';
import {
  adminPricingRulesApi,
  fetchServiceTypes,
  readSession,
  type AdminPricingRule,
  type AdminPricingRuleDraft,
  type AdminPricingRuleType,
  type ServiceTypeSummary,
} from '../lib/api.js';
import {
  effectiveRolesOf,
  rolesHavePermission,
} from '../lib/rbac-matrix.js';
import './PricingRules.css';

interface PricingRulesProps {
  config: ClientConfig;
}

type Status = 'loading' | 'error' | 'ready';

const RULE_TYPE_LABELS: Record<string, string> = {
  percentage_discount: 'Percentage discount',
  fixed_discount: 'Fixed discount',
  surcharge: 'Surcharge',
};

const DAY_LABELS = [
  'Sundays',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
];

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * The human sentence a rule means — derived from the SAME fields the engine
 * executes (Principle 1: no second phrasing to drift).
 */
export function ruleSentence(
  rule: Pick<
    AdminPricingRule,
    'type' | 'percentage' | 'valueCents' | 'minNights' | 'minDogs' | 'dayOfWeek'
  >,
  serviceName: string | null,
): string {
  const effect =
    rule.type === 'percentage_discount' && rule.percentage !== null
      ? `${rule.percentage}% off`
      : rule.type === 'fixed_discount' && rule.valueCents !== null
        ? `${dollars(rule.valueCents)} off`
        : rule.type === 'surcharge' && rule.valueCents !== null
          ? `${dollars(rule.valueCents)} surcharge on`
          : 'Adjusts';
  const service = serviceName ?? 'this service';
  const conditions: string[] = [];
  if (rule.minNights !== null) conditions.push(`stays of ${rule.minNights}+ nights`);
  if (rule.minDogs !== null) conditions.push(`${rule.minDogs}+ dogs`);
  if (rule.dayOfWeek !== null) conditions.push(DAY_LABELS[rule.dayOfWeek] ?? '');
  const cond = conditions.filter(Boolean).join(', ');
  return cond ? `${effect} ${service} — ${cond}` : `${effect} ${service}`;
}

interface RuleFormState {
  serviceTypeId: string;
  name: string;
  type: AdminPricingRuleType;
  amountDollars: string;
  percentage: string;
  minNights: string;
  minDogs: string;
  dayOfWeek: string;
  priority: string;
  isActive: boolean;
}

const EMPTY_FORM: RuleFormState = {
  serviceTypeId: '',
  name: '',
  type: 'percentage_discount',
  amountDollars: '',
  percentage: '',
  minNights: '',
  minDogs: '',
  dayOfWeek: '',
  priority: '0',
  isActive: true,
};

function formFromRule(rule: AdminPricingRule): RuleFormState {
  return {
    serviceTypeId: rule.serviceTypeId,
    name: rule.name,
    type:
      rule.type === 'fixed_discount' || rule.type === 'surcharge'
        ? rule.type
        : 'percentage_discount',
    amountDollars:
      rule.valueCents !== null ? (rule.valueCents / 100).toFixed(2) : '',
    percentage: rule.percentage !== null ? String(rule.percentage) : '',
    minNights: rule.minNights !== null ? String(rule.minNights) : '',
    minDogs: rule.minDogs !== null ? String(rule.minDogs) : '',
    dayOfWeek: rule.dayOfWeek !== null ? String(rule.dayOfWeek) : '',
    priority: String(rule.priority),
    isActive: rule.isActive,
  };
}

function validateForm(f: RuleFormState, forCreate: boolean): string | null {
  if (forCreate && !f.serviceTypeId) return 'Pick the service this rule prices.';
  if (!f.name.trim()) return 'Give the rule a name staff will recognise.';
  if (f.type === 'percentage_discount') {
    const p = Number(f.percentage);
    if (!Number.isFinite(p) || p <= 0 || p > 100) {
      return 'Percentage must be between 0 and 100.';
    }
  } else {
    const a = Number(f.amountDollars);
    if (!Number.isFinite(a) || a <= 0) {
      return 'Enter the dollar amount for this rule.';
    }
  }
  for (const [label, raw] of [
    ['Minimum nights', f.minNights],
    ['Minimum dogs', f.minDogs],
  ] as const) {
    if (raw !== '' && (!Number.isInteger(Number(raw)) || Number(raw) < 2)) {
      return `${label} must be a whole number of at least 2.`;
    }
  }
  return null;
}

function draftFromForm(f: RuleFormState): AdminPricingRuleDraft {
  return {
    serviceTypeId: f.serviceTypeId,
    name: f.name.trim(),
    type: f.type,
    ...(f.type === 'percentage_discount'
      ? { percentage: Number(f.percentage) }
      : { valueCents: Math.round(Number(f.amountDollars) * 100) }),
    ...(f.minNights !== '' ? { minNights: Number(f.minNights) } : {}),
    ...(f.minDogs !== '' ? { minDogs: Number(f.minDogs) } : {}),
    ...(f.dayOfWeek !== '' ? { dayOfWeek: Number(f.dayOfWeek) } : {}),
    ...(f.priority !== '' ? { priority: Number(f.priority) } : {}),
    isActive: f.isActive,
  };
}

export function PricingRules({ config }: PricingRulesProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const canEdit = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'business-config.pricing:write:all',
  );

  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rules, setRules] = useState<AdminPricingRule[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeSummary[]>([]);

  // Modal form (create + edit share it). `editing` null = create.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPricingRule | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Two-tap delete (the Transactions void pattern — no browser confirm()).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const serviceNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of serviceTypes) m.set(s.id, s.display_name || s.name);
    return m;
  }, [serviceTypes]);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    const { token } = readSession();
    const [rulesRes, types] = await Promise.all([
      adminPricingRulesApi.list(apiUrl),
      token
        ? fetchServiceTypes(apiUrl, token).catch(
            () => [] as ServiceTypeSummary[],
          )
        : Promise.resolve([] as ServiceTypeSummary[]),
    ]);
    if (rulesRes.error || !rulesRes.data) {
      setLoadError(rulesRes.error ?? 'Could not load pricing rules.');
      setStatus('error');
      return;
    }
    setRules(rulesRes.data.rules ?? []);
    setServiceTypes(types);
    setStatus('ready');
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, serviceTypeId: serviceTypes[0]?.id ?? '' });
    setFormError(null);
    setFormOpen(true);
  }, [serviceTypes]);

  const openEdit = useCallback((rule: AdminPricingRule) => {
    setEditing(rule);
    setForm(formFromRule(rule));
    setFormError(null);
    setFormOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    const invalid = validateForm(form, editing === null);
    if (invalid) {
      setFormError(invalid);
      return;
    }
    setIsSaving(true);
    setFormError(null);
    const draft = draftFromForm(form);
    const res = editing
      ? await adminPricingRulesApi.update(apiUrl, editing.id, {
          name: draft.name,
          type: draft.type,
          ...(draft.percentage !== undefined
            ? { percentage: draft.percentage }
            : {}),
          ...(draft.valueCents !== undefined
            ? { valueCents: draft.valueCents }
            : {}),
          ...(draft.minNights !== undefined ? { minNights: draft.minNights } : {}),
          ...(draft.minDogs !== undefined ? { minDogs: draft.minDogs } : {}),
          ...(draft.dayOfWeek !== undefined ? { dayOfWeek: draft.dayOfWeek } : {}),
          ...(draft.priority !== undefined ? { priority: draft.priority } : {}),
          isActive: draft.isActive,
        })
      : await adminPricingRulesApi.create(apiUrl, draft);
    setIsSaving(false);
    if (res.error || !res.data?.rule) {
      setFormError(res.error ?? 'Could not save the rule.');
      return;
    }
    setFormOpen(false);
    await load();
  }, [apiUrl, editing, form, load]);

  const handleDelete = useCallback(
    async (id: string) => {
      setRowError(null);
      const res = await adminPricingRulesApi.remove(apiUrl, id);
      setConfirmDeleteId(null);
      if (res.error) {
        setRowError(res.error);
        return;
      }
      await load();
    },
    [apiUrl, load],
  );

  const toggleActive = useCallback(
    async (rule: AdminPricingRule) => {
      setRowError(null);
      const res = await adminPricingRulesApi.update(apiUrl, rule.id, {
        isActive: !rule.isActive,
      });
      if (res.error) {
        setRowError(res.error);
        return;
      }
      await load();
    },
    [apiUrl, load],
  );

  // Live preview from the CURRENT form values — the sentence the rule will
  // mean, visible before saving (Principle 1).
  const previewSentence = ruleSentence(
    {
      type: form.type,
      valueCents:
        form.type !== 'percentage_discount' && form.amountDollars !== ''
          ? Math.round(Number(form.amountDollars) * 100)
          : null,
      percentage:
        form.type === 'percentage_discount' && form.percentage !== ''
          ? Number(form.percentage)
          : null,
      minDogs: form.minDogs !== '' ? Number(form.minDogs) : null,
      minNights: form.minNights !== '' ? Number(form.minNights) : null,
      dayOfWeek: form.dayOfWeek !== '' ? Number(form.dayOfWeek) : null,
    },
    serviceNameById.get(form.serviceTypeId) ?? null,
  );

  return (
    <div className="ferro-page" data-testid="pricing-rules-page">
      <PageHeader
        title="Pricing rules"
        subtitle="Automatic discounts and surcharges — set the rule once and every eligible booking prices itself."
        actions={
          canEdit ? (
            <Button
              variant="primary"
              onClick={openCreate}
              disabled={status !== 'ready'}
              data-testid="pricing-rules-new"
            >
              New rule
            </Button>
          ) : undefined
        }
      />

      {!canEdit ? (
        <p className="ferro-muted" data-testid="pricing-rules-readonly-note">
          You can review the facility's pricing rules here. Creating or
          changing them is an owner action.
        </p>
      ) : null}

      {status === 'loading' ? (
        <div data-testid="pricing-rules-loading">
          <Card>
            <Skeleton variant="text" lines={4} />
          </Card>
        </div>
      ) : null}

      {status === 'error' ? (
        <div data-testid="pricing-rules-error">
          <Alert variant="error">
            {loadError}{' '}
            <Button variant="ghost" onClick={() => void load()}>
              Retry
            </Button>
          </Alert>
        </div>
      ) : null}

      {status === 'ready' && rules.length === 0 ? (
        <div data-testid="pricing-rules-empty">
          <EmptyState
            title="No automatic pricing rules yet"
            description='Rules price bookings for you — e.g. "10% off boarding stays of 10 nights or more" or a multi-dog family discount.'
            action={
              canEdit
                ? { label: 'Create the first rule', onClick: openCreate }
                : undefined
            }
          />
        </div>
      ) : null}

      {rowError ? (
        <div data-testid="pricing-rules-row-error">
          <Alert variant="error">{rowError}</Alert>
        </div>
      ) : null}

      {status === 'ready' && rules.length > 0 ? (
        <div data-testid="pricing-rules-list">
          <Card>
            <ul className="ferro-pricing-rules__list">
              {rules.map((rule) => (
                <li
                  key={rule.id}
                  className="ferro-pricing-rules__row"
                  data-testid={`pricing-rule-${rule.id}`}
                >
                  <div className="ferro-pricing-rules__body">
                    <div className="ferro-pricing-rules__sentence">
                      {ruleSentence(
                        rule,
                        serviceNameById.get(rule.serviceTypeId) ?? null,
                      )}
                    </div>
                    <div className="ferro-muted">
                      {rule.name}
                      {' · '}
                      {RULE_TYPE_LABELS[rule.type] ?? rule.type}
                      {rule.priority > 0 ? ` · priority ${rule.priority}` : ''}
                    </div>
                  </div>
                  <div className="ferro-pricing-rules__badges">
                    {!rule.editable ? (
                      <Badge variant="platform">Platform</Badge>
                    ) : null}
                    <Badge variant={rule.isActive ? 'active' : 'closed'}>
                      {rule.isActive ? 'Active' : 'Off'}
                    </Badge>
                  </div>
                  {canEdit && rule.editable ? (
                    <div className="ferro-pricing-rules__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void toggleActive(rule)}
                        data-testid={`pricing-rule-toggle-${rule.id}`}
                      >
                        {rule.isActive ? 'Turn off' : 'Turn on'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(rule)}
                        data-testid={`pricing-rule-edit-${rule.id}`}
                      >
                        Edit
                      </Button>
                      {confirmDeleteId === rule.id ? (
                        <>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => void handleDelete(rule.id)}
                            data-testid={`pricing-rule-delete-confirm-${rule.id}`}
                          >
                            Confirm delete
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            Keep
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDeleteId(rule.id)}
                          data-testid={`pricing-rule-delete-${rule.id}`}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}

      <Modal
        isOpen={formOpen}
        onClose={() => {
          if (!isSaving) setFormOpen(false);
        }}
        title={editing ? 'Edit pricing rule' : 'New pricing rule'}
        closeOnBackdropClick={!isSaving}
        closeOnEsc={!isSaving}
      >
        <div className="ferro-form" data-testid="pricing-rule-form">
          {editing === null ? (
            <Select
              id="pricing-rule-service"
              label="Service"
              value={form.serviceTypeId}
              onChange={(e) =>
                setForm((f) => ({ ...f, serviceTypeId: e.target.value }))
              }
              placeholder="Choose a service…"
              options={serviceTypes.map((s) => ({
                value: s.id,
                label: s.display_name || s.name,
              }))}
              data-testid="pricing-rule-service"
            />
          ) : null}

          <label className="ferro-field">
            <span className="ferro-field__label">Rule name</span>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="10-night stay discount"
              data-testid="pricing-rule-name"
            />
          </label>

          <Select
            id="pricing-rule-type"
            label="Type"
            value={form.type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                type: e.target.value as AdminPricingRuleType,
              }))
            }
            options={[
              { value: 'percentage_discount', label: 'Percentage discount' },
              { value: 'fixed_discount', label: 'Fixed discount ($)' },
              { value: 'surcharge', label: 'Surcharge ($)' },
            ]}
            data-testid="pricing-rule-type"
          />

          {form.type === 'percentage_discount' ? (
            <label className="ferro-field">
              <span className="ferro-field__label">Percent off</span>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={form.percentage}
                onChange={(e) =>
                  setForm((f) => ({ ...f, percentage: e.target.value }))
                }
                placeholder="10"
                data-testid="pricing-rule-percentage"
              />
            </label>
          ) : (
            <label className="ferro-field">
              <span className="ferro-field__label">Amount (dollars)</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.amountDollars}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amountDollars: e.target.value }))
                }
                placeholder="20.00"
                data-testid="pricing-rule-amount"
              />
            </label>
          )}

          <div className="ferro-form__row">
            <label className="ferro-field">
              <span className="ferro-field__label">Min nights (boarding)</span>
              <Input
                type="number"
                min="2"
                value={form.minNights}
                onChange={(e) =>
                  setForm((f) => ({ ...f, minNights: e.target.value }))
                }
                placeholder="10"
                data-testid="pricing-rule-min-nights"
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Min dogs</span>
              <Input
                type="number"
                min="2"
                value={form.minDogs}
                onChange={(e) =>
                  setForm((f) => ({ ...f, minDogs: e.target.value }))
                }
                placeholder="2"
                data-testid="pricing-rule-min-dogs"
              />
            </label>
          </div>

          <div className="ferro-form__row">
            <Select
              id="pricing-rule-day"
              label="Day of week"
              value={form.dayOfWeek}
              onChange={(e) =>
                setForm((f) => ({ ...f, dayOfWeek: e.target.value }))
              }
              options={[
                { value: '', label: 'Any day' },
                ...DAY_LABELS.map((label, i) => ({
                  value: String(i),
                  label,
                })),
              ]}
              data-testid="pricing-rule-day"
            />
            <label className="ferro-field">
              <span className="ferro-field__label">Priority</span>
              <Input
                type="number"
                min="0"
                value={form.priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: e.target.value }))
                }
                data-testid="pricing-rule-priority"
              />
            </label>
          </div>

          <label className="ferro-field ferro-field--inline">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) =>
                setForm((f) => ({ ...f, isActive: e.target.checked }))
              }
              data-testid="pricing-rule-active"
            />
            <span className="ferro-field__label">Active</span>
          </label>

          <p className="ferro-muted" data-testid="pricing-rule-preview">
            {previewSentence}
          </p>

          {formError ? (
            <div data-testid="pricing-rule-form-error">
              <Alert variant="error">{formError}</Alert>
            </div>
          ) : null}

          <div className="ferro-form__actions">
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              isLoading={isSaving}
              data-testid="pricing-rule-save"
            >
              {editing ? 'Save changes' : 'Create rule'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setFormOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
